import { Accordion, Alert, AlertIcon, Card, Divider, Heading, Stack, useDisclosure, VStack } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Activity, ChecksRecord, DetailPageTask, FileData, Task, WorkshopVerificationRequest } from 'api/types';
import ActivityLogCard from 'components/ActivityLogCard';
import Link from 'components/Link';
import { LoadingSpinner } from 'components/LoadingSpinner';
import { VerificationStatusCard } from 'components/VerificationStatusCard';
import { UserPermissions } from 'configs/routes';
import { useUserContext } from 'context/UserContextProvider';
import { downloadFile } from 'helpers/downloadHelper';
import { UploadedFileInfo } from 'hooks/mutations/useFileUploadMutation';
import useFileQueryV2 from 'hooks/useFileQueryV2';
import useWorkshopVerificationRequestQuery from 'hooks/useWorkshopVerificationRequestQuery';
import { get } from 'lodash';
import TaskBannerCard from 'pages/tasks/TaskBannerCard';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

import { TasksTableCard } from '../../components/TasksTableCard';
import DocumentAccordionPanel from './components/DocumentAccordionPanel';
import DocumentStatusIcon from './components/DocumentStatusIcon';
import { ManualPoaUploadModal } from './components/ManualPoaUploadModal';
import VerificationActionButtons from './components/VerificationActionButtons';
import VerificationDetailsGrid from './components/VerificationDetailsGrid';
import VerificationModals from './components/VerificationModals';
import { useDocumentUpload } from './hooks/useDocumentUpload';
import { useVerificationActions } from './hooks/useVerificationActions';
import { type DocumentType, NOT_READABLE_KEY, VerificationStatus } from './types';

type ChecklistState = Record<string, 'valid' | 'error' | null>;

type PanelDocumentType = 'business_licence' | 'identification' | 'handwerkskarte' | 'power_of_attorney';

function getPanelIndex(documentType: PanelDocumentType, hasHandwerkskarte: boolean): number {
  const renderedPanels: PanelDocumentType[] = [
    'business_licence',
    'identification',
    ...(hasHandwerkskarte ? (['handwerkskarte'] as const) : []),
    'power_of_attorney',
  ];

  return renderedPanels.indexOf(documentType);
}

const verificationSchema = yup
  .object({
    business_licence: yup.mixed(),
    identification: yup.mixed(),
    power_of_attorney: yup.mixed(),
    handwerkskarte: yup.mixed(),
    checklist: yup
      .object({
        identification: yup.mixed<ChecklistState>().default({}),
        business_licence: yup.mixed<ChecklistState>().default({}),
        power_of_attorney: yup.mixed<ChecklistState>().default({}),
        handwerkskarte: yup.mixed<ChecklistState>().default({}),
      })
      .default({}),
  })
  .required();

const WorkshopVerificationDetailPage = () => {
  const { t } = useTranslation();
  const { id: workshopId } = useParams();
  const navigate = useNavigate();
  const { user, opUsers } = useUserContext();
  const topRef = useRef<HTMLDivElement>(null);

  // --- File download state ---
  const [url, setUrl] = useState<string | undefined>(undefined);
  const [downloadDocument, setDownloadDocument] = useState<FileData | undefined>(undefined);
  const [click, setClick] = useState(false);
  const fileQuery = useFileQueryV2(url);

  useEffect(() => {
    downloadFile(downloadDocument, fileQuery.data);
    setClick(false);
  }, [fileQuery.data, click, downloadDocument]);

  // --- Modal state ---
  const { isOpen: isRejectOpen, onOpen: onOpenReject, onClose: onCloseReject } = useDisclosure();
  const { isOpen: isApproveOpen, onOpen: onOpenApprove, onClose: onCloseApprove } = useDisclosure();
  const { isOpen: isManualPoaUploadOpen, onOpen: onOpenManualPoaUpload, onClose: onCloseManualPoaUpload } = useDisclosure();
  const { isOpen: isIdReplacementOpen, onOpen: onOpenIdReplacement, onClose: onCloseIdReplacement } = useDisclosure();
  const { isOpen: isBlReplacementOpen, onOpen: onOpenBlReplacement, onClose: onCloseBlReplacement } = useDisclosure();
  const { isOpen: isPoaReplacementOpen, onOpen: onOpenPoaReplacement, onClose: onClosePoaReplacement } = useDisclosure();
  const { isOpen: isHandelskarteReplacementOpen, onOpen: onOpenHkReplacement, onClose: onCloseHkReplacement } = useDisclosure();
  const { isOpen: isVerifiedReplacementOpen, onOpen: onOpenVerifiedReplacement, onClose: onCloseVerifiedReplacement } = useDisclosure();
  const {
    isOpen: isClarificationReplacementOpen,
    onOpen: onOpenClarificationReplacement,
    onClose: onCloseClarificationReplacement,
  } = useDisclosure();

  const [reasonForReplacement, setReasonForReplacement] = useState('');
  const [pendingSubmitDocType, setPendingSubmitDocType] = useState<DocumentType | null>(null);

  // --- Form ---
  const methods = useForm({
    resolver: yupResolver(verificationSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      business_licence: undefined,
      identification: undefined,
      power_of_attorney: undefined,
      handwerkskarte: undefined,
      checklist: {
        identification: {},
        business_licence: {},
        power_of_attorney: {},
        handwerkskarte: {},
      },
    },
  });
  const { watch, setValue, getValues } = methods;

  // --- Query ---
  const workshopVerificationQuery = useWorkshopVerificationRequestQuery(workshopId);
  const tasks = (workshopVerificationQuery.data?.data as { tasks?: DetailPageTask[] } | undefined)?.tasks;

  // The OpenAPI-generated schema has looser types than what the API actually returns.
  // Cast to the more precise manual type which matches the real response shape.
  const verificationData = workshopVerificationQuery?.data?.data as unknown as WorkshopVerificationRequest | undefined;

  // --- Actions Hook ---
  const { mutations, actions, ...derived } = useVerificationActions({
    workshopId,
    verificationData,
    currentUserId: user?.id,
    topRef,
  });

  // --- Initialize checklist from server data ---
  useEffect(() => {
    if (!verificationData) return;

    const initDoc = (
      docType: DocumentType,
      checksData: ChecksRecord | undefined,
      initialErrors: string[] | undefined,
      documentStatus: boolean | undefined,
    ) => {
      if (!checksData) return;
      const notTouched = !documentStatus && (initialErrors?.length ?? 0) === 0;
      if (notTouched) return;

      for (const key of Object.keys(checksData)) {
        setValue(`checklist.${docType}.${key}`, initialErrors?.includes(key) ? 'error' : 'valid');
      }
    };

    initDoc('identification', verificationData.identification_checks, verificationData.id_errors, verificationData.identification_status);
    initDoc('business_licence', verificationData.business_licence_checks, verificationData.bl_errors, verificationData.business_licence_status);
    initDoc('power_of_attorney', verificationData.power_of_attorney_checks, verificationData.poa_errors, verificationData.power_of_attorney_status);
    initDoc('handwerkskarte', verificationData.handwerkskarte_checks, verificationData.hk_errors, verificationData.handwerkskarte_status);
  }, [verificationData, setValue]);

  // --- Checklist helpers (reactive, for render-time) ---
  const checklistValues = watch('checklist') as Record<string, Record<string, string>> | undefined;

  const getChecklistArrays = useMemo(() => {
    return (docType: DocumentType, checksData: ChecksRecord | undefined) => {
      const allKeys = checksData ? Object.keys(checksData) : [];
      const values = checklistValues?.[docType] ?? {};
      return {
        valid: allKeys.filter((k) => values[k] === 'valid'),
        err: allKeys.filter((k) => values[k] === 'error'),
        allKeys,
      };
    };
  }, [checklistValues]);

  // --- Callback helper (uses getValues for fresh state) ---
  const getChecklistErr = (docType: DocumentType, checksData: ChecksRecord | undefined): string[] => {
    const allKeys = checksData ? Object.keys(checksData) : [];
    const values = (getValues(`checklist.${docType}`) as Record<string, string>) ?? {};
    return allKeys.filter((k) => values[k] === 'error');
  };

  const checksDataMap: Record<DocumentType, ChecksRecord | undefined> = {
    identification: verificationData?.identification_checks,
    business_licence: verificationData?.business_licence_checks,
    power_of_attorney: verificationData?.power_of_attorney_checks,
    handwerkskarte: verificationData?.handwerkskarte_checks,
  };

  const clearChecklistForDoc = (docType: DocumentType) => {
    const keys = checksDataMap[docType] ? Object.keys(checksDataMap[docType]!) : [];
    keys.forEach((k) => setValue(`checklist.${docType}.${k}`, null));
  };

  // --- Document Uploads ---
  const idUpload = useDocumentUpload({ watchedFormField: watch('identification'), fileUploadMutation: mutations.fileUploadMutation });
  const blUpload = useDocumentUpload({ watchedFormField: watch('business_licence'), fileUploadMutation: mutations.fileUploadMutation });
  const poaUpload = useDocumentUpload({ watchedFormField: watch('power_of_attorney'), fileUploadMutation: mutations.fileUploadMutation });
  const hkUpload = useDocumentUpload({ watchedFormField: watch('handwerkskarte'), fileUploadMutation: mutations.fileUploadMutation });

  // --- Permissions ---
  const canReplaceDocumentsCompletedVerification =
    derived.isCompleted &&
    (user?.permissions.includes('replace_documents') || (Array.isArray(user?.roles) && user.roles.some((role) => role.name === 'operations')));

  const canReplaceDocumentsInClarificationRoleBased = user?.permissions.includes('workshop-verification-requests:replace-documents');

  const canManuallyUploadPoa = !!user?.permissions.includes(UserPermissions.VERIFICATIONSMANUALLYUPLOADPOA);

  const canReplaceInClarificationStep1 = derived.isInClarificationStep1 && !!canReplaceDocumentsInClarificationRoleBased;
  const canReplaceInClarificationStep2 = derived.isInClarificationStep2 && !!canReplaceDocumentsInClarificationRoleBased;

  const isGermanWorkshop = verificationData?.workshop?.country?.toUpperCase() === 'DE';
  const showHandwerkskartePanel = isGermanWorkshop && verificationData?.handwerkskarte?.id != null;

  // --- Accordion State ---
  const [accordionIndex, setAccordionIndex] = useState<number[]>([]);

  useEffect(() => {
    const panelIndex = (documentType: PanelDocumentType) => getPanelIndex(documentType, showHandwerkskartePanel);

    const openStepOne = () => {
      const panels: number[] = [];
      if (!verificationData?.business_licence_status) panels.push(panelIndex('business_licence'));
      if (!verificationData?.identification_status) panels.push(panelIndex('identification'));
      if (showHandwerkskartePanel && !verificationData?.handwerkskarte_status) {
        panels.push(panelIndex('handwerkskarte'));
      }
      return panels.sort((a, b) => a - b);
    };

    let newIndex: number[] = [];
    if (derived.isCompleted) {
      newIndex = [];
    } else if (derived.onStepOne) {
      newIndex = openStepOne();
    } else if (
      (!derived.onStepOne &&
        verificationData?.status !== VerificationStatus.WAITING_ON_POA &&
        derived.onStepTwo &&
        !derived.isCompleted &&
        !verificationData?.power_of_attorney_status) ||
      verificationData?.status === VerificationStatus.CLARIFICATION_STEP_2
    ) {
      newIndex = [panelIndex('power_of_attorney')];
    } else if (derived.onStepTwo && verificationData?.power_of_attorney_status) {
      newIndex = [];
    } else if (verificationData?.status === VerificationStatus.CLARIFICATION_STEP_1) {
      newIndex = openStepOne();
    }
    setAccordionIndex(newIndex);
  }, [
    derived.isCompleted,
    derived.onStepOne,
    derived.onStepTwo,
    verificationData?.identification_status,
    verificationData?.business_licence_status,
    verificationData?.power_of_attorney_status,
    verificationData?.handwerkskarte_status,
    showHandwerkskartePanel,
    verificationData?.status,
    mutations.clarificationMutation.isPending,
    mutations.approveMutation.isPending,
    mutations.updateDocsMutation.isPending,
  ]);

  // --- Document submit helpers ---
  const makeDocumentSubmitHandler = (docType: DocumentType, fileUuid: string | undefined, uploadReset: () => void) => {
    return () => {
      if (canReplaceDocumentsCompletedVerification) {
        setPendingSubmitDocType(docType);
        setReasonForReplacement('');
        onOpenVerifiedReplacement();
      } else {
        const docErr = getChecklistErr(docType, checksDataMap[docType]);
        actions.submitDocument(docType, docErr, fileUuid, undefined, {
          onSuccess: () => {
            uploadReset();
            clearChecklistForDoc(docType);
          },
        });
      }
    };
  };

  const handleVerifiedReplacementConfirm = () => {
    if (!pendingSubmitDocType) return;
    const uploadMap = { identification: idUpload, business_licence: blUpload, power_of_attorney: poaUpload, handwerkskarte: hkUpload };
    const up = uploadMap[pendingSubmitDocType];
    const errKeys = getChecklistErr(pendingSubmitDocType, checksDataMap[pendingSubmitDocType]);

    actions.submitDocument(pendingSubmitDocType, errKeys, up.file?.uuid || undefined, reasonForReplacement, {
      onSuccess: () => {
        onCloseVerifiedReplacement();
        up.reset();
      },
    });
  };

  // --- Replacement handlers ---
  const onIdReplacement = () => {
    clearChecklistForDoc('identification');
    idUpload.setIsReplacing(true);
    idUpload.setFile(null);
    onCloseIdReplacement();
  };
  const onHandelskarteReplacement = () => {
    clearChecklistForDoc('handwerkskarte');
    hkUpload.setIsReplacing(true);
    hkUpload.setFile(null);
    onCloseHkReplacement();
  };
  const onBlReplacement = () => {
    clearChecklistForDoc('business_licence');
    blUpload.setIsReplacing(true);
    blUpload.setFile(null);
    onCloseBlReplacement();
  };
  const onPoaReplacement = () => {
    clearChecklistForDoc('power_of_attorney');
    poaUpload.setIsReplacing(true);
    poaUpload.setFile(null);
    onClosePoaReplacement();
  };

  // --- Download handler ---
  const makeDownloadHandler = (fileId: number | undefined, document: FileData | undefined) => () => {
    setUrl(`workshop-verification-request/file/${fileId}/download`);
    setDownloadDocument(document);
    setClick(true);
  };

  // --- Save-disabled logic ---
  const computeIsSaveDisabled = (
    checklistState: { valid: string[]; err: string[]; allKeys: string[] },
    docStatus: boolean | undefined,
    docErrors: string[] | undefined,
    uploadedFile: UploadedFileInfo | null,
    canClarificationReplace: boolean,
  ) => {
    const { valid, err, allKeys } = checklistState;
    const hasNewFile = !!uploadedFile?.base64?.encodedFile;

    if (canClarificationReplace && hasNewFile) return false;

    if (!derived.isOpenTaskAssignedToCurrentUser && !canReplaceDocumentsCompletedVerification) return true;

    const isNotReadable = err.includes(NOT_READABLE_KEY);
    if (isNotReadable) {
      // When document is not readable, only the not_readable decision is required
    } else {
      if (allKeys.length !== valid.length + err.length) return true;
    }

    if (docStatus && allKeys.length === valid.length && !hasNewFile) return true;

    if (
      Array.isArray(err) &&
      Array.isArray(docErrors) &&
      ((err.length === 0 && docErrors.length === 0) ||
        (err.length === docErrors.length && err.every((val) => docErrors.includes(val)) && docErrors.every((val) => err.includes(val))))
    ) {
      return true;
    }

    return false;
  };

  // --- Navigation Guards ---
  const workshopIdNumber = Number(workshopId);
  if (isNaN(workshopIdNumber)) {
    navigate('/404', { replace: true });
    return null;
  }
  if (workshopVerificationQuery.isLoading) return <LoadingSpinner />;
  if (workshopVerificationQuery.isError) {
    navigate('/404', { replace: true });
    return null;
  }

  return (
    <FormProvider {...(methods as unknown as ReturnType<typeof useForm<FieldValues>>)}>
      <div ref={topRef} />

      {/* Status Banners */}
      {derived.isSystemPartnerFull && (
        <Alert status="warning" variant="solid" colorScheme="purple">
          <AlertIcon />
          <Trans i18nKey="workshop_verification_request:system_partner_full" values={{ systemPartnerName: derived.systempartner?.name }} />
        </Alert>
      )}
      {derived.isCompleted && (
        <Alert status="success" variant="solid">
          <AlertIcon />
          {t('workshop_verification_request:verification_completed')}
        </Alert>
      )}
      {derived.isRejected && (
        <Alert status="error" variant="solid">
          <AlertIcon />
          {t('workshop_verification_request:reject_verification.banner')}
        </Alert>
      )}
      {derived.waitingOnPoa && (
        <Alert status="info" variant="solid" colorScheme="gray">
          <AlertIcon />
          {t('workshop_verification_request:verification_pending_poa')}
        </Alert>
      )}
      {derived.isVerificationDataChange && (
        <Alert status="info" variant="solid" bg="brand.500">
          <AlertIcon />
          {t('workshop_verification_request:verification_data_change')}
          {derived.updateRequest && (
            <Link to={`/app/op/workshop-update-request/${derived.updateRequest.id}`} ml="2" color="white" textDecoration="underline">
              {derived.updateRequest.user_readable_id}
            </Link>
          )}
        </Alert>
      )}

      {/* Task Banner */}
      {derived.hasOpenTask && (
        <TaskBannerCard
          queryToInvalidate={actions.queryToInvalidate}
          opUsers={opUsers}
          currentUserId={user?.id}
          assignee={derived.openTask?.assignee}
          isLoading={workshopVerificationQuery.isFetching || mutations.assignMutation.isPending}
          assign={() =>
            mutations.assignMutation.mutate({
              updated_at: get(verificationData, 'open_task.updated_at', verificationData?.updated_at)!,
            })
          }
          reassign={(user_id) =>
            mutations.assignMutation.mutate({
              force: true,
              user_id,
              updated_at: get(verificationData, 'open_task.updated_at', verificationData?.updated_at)!,
            })
          }
          task={derived.openTask as Task}
        />
      )}

      {/* Main Card */}
      <Card m={6}>
        <VStack p={6} alignItems="flex-start" spacing={6}>
          <VerificationStatusCard
            completedAt={verificationData?.completed_at}
            currentStatus={verificationData?.status}
            currentStatusReason={verificationData?.status_reason}
            resourceKey="workshop_verification_request"
            workshopVerificationQueryData={verificationData ?? {}}
          />
          <Divider color="gray.200" />
          <Heading as="h3" size="xxs">
            {t('workshop_verification_request:details.label')}
          </Heading>
          <VerificationDetailsGrid
            verificationData={verificationData!}
            updateRequest={derived.updateRequest}
            durNotCompleted={derived.durNotCompleted}
            isStep1={derived.isStep1}
          />
        </VStack>

        <Stack w="full" direction={['column']} p={6}>
          <Divider color="gray.200" />
          <Heading as="h3" size="xxs" py={6}>
            {t('workshop_verification_request:documents.label')}
          </Heading>

          <Accordion
            defaultIndex={accordionIndex}
            index={accordionIndex}
            onChange={(expandedIndex) => {
              if (Array.isArray(expandedIndex)) setAccordionIndex(expandedIndex);
              else if (typeof expandedIndex === 'number') setAccordionIndex([expandedIndex]);
              else setAccordionIndex([]);
            }}
            allowMultiple
          >
            {/* Business Licence */}
            <DocumentAccordionPanel
              documentType="business_licence"
              label={t('workshop_verification_request:bl.label')}
              checksData={verificationData?.business_licence_checks}
              documentFile={verificationData?.business_licence}
              uploadedFile={blUpload.file}
              isReplacing={blUpload.isReplacing}
              buttonsLocked={derived.buttonsLockedStepOne}
              isClarificationReplacement={canReplaceInClarificationStep1}
              canReplace={derived.isStep1 && derived.isOpenTaskAssignedToCurrentUser && !derived.isInClarification}
              canReplaceCompleted={!!canReplaceDocumentsCompletedVerification}
              isUploadPending={mutations.fileUploadMutation.isPending}
              isSavePending={mutations.updateDocsMutation.isPending || workshopVerificationQuery.isPending}
              isDisabled={derived.isRejected && verificationData?.business_licence?.id === null}
              currentStatus={verificationData?.status}
              isStep1={derived.isStep1}
              statusIcon={
                <DocumentStatusIcon
                  notTouched={!!derived.blNotTouched}
                  hasErrors={(verificationData?.bl_errors?.length ?? 0) > 0}
                  documentStatus={verificationData?.business_licence_status}
                  hasDocument
                  isStep1={derived.isStep1}
                  currentStatus={verificationData?.status}
                />
              }
              onOpenReplacement={onOpenBlReplacement}
              onDownload={makeDownloadHandler(verificationData?.business_licence?.id, verificationData?.business_licence)}
              onDiscard={() => {
                clearChecklistForDoc('business_licence');
                blUpload.reset();
              }}
              onSave={makeDocumentSubmitHandler('business_licence', blUpload.file?.uuid || undefined, blUpload.reset)}
              isSaveDisabled={computeIsSaveDisabled(
                getChecklistArrays('business_licence', verificationData?.business_licence_checks),
                verificationData?.business_licence_status,
                verificationData?.bl_errors,
                blUpload.file,
                canReplaceInClarificationStep1,
              )}
              isDiscardDisabled={
                verificationData?.status !== VerificationStatus.STEP_1_REVIEW ||
                (getChecklistArrays('business_licence', verificationData?.business_licence_checks).valid.length === 0 &&
                  getChecklistArrays('business_licence', verificationData?.business_licence_checks).err.length === 0)
              }
            />

            {/* Identification */}
            <DocumentAccordionPanel
              documentType="identification"
              label={t('workshop_verification_request:id.label')}
              checksData={verificationData?.identification_checks}
              documentFile={verificationData?.identification}
              uploadedFile={idUpload.file}
              isReplacing={idUpload.isReplacing}
              buttonsLocked={derived.buttonsLockedStepOne}
              isClarificationReplacement={canReplaceInClarificationStep1}
              canReplace={derived.isStep1 && derived.isOpenTaskAssignedToCurrentUser && !derived.isInClarification}
              canReplaceCompleted={!!canReplaceDocumentsCompletedVerification}
              isUploadPending={mutations.fileUploadMutation.isPending}
              isSavePending={mutations.updateDocsMutation.isPending || workshopVerificationQuery.isPending}
              isDisabled={derived.isRejected && verificationData?.identification?.id === null}
              currentStatus={verificationData?.status}
              isStep1={derived.isStep1}
              statusIcon={
                <DocumentStatusIcon
                  notTouched={!!derived.idNotTouched}
                  hasErrors={(verificationData?.id_errors?.length ?? 0) > 0}
                  documentStatus={verificationData?.identification_status}
                  hasDocument
                  isStep1={derived.isStep1}
                  currentStatus={verificationData?.status}
                />
              }
              onOpenReplacement={onOpenIdReplacement}
              onDownload={makeDownloadHandler(verificationData?.identification?.id, verificationData?.identification)}
              onDiscard={() => {
                clearChecklistForDoc('identification');
                idUpload.reset();
              }}
              onSave={makeDocumentSubmitHandler('identification', idUpload.file?.uuid || undefined, idUpload.reset)}
              isSaveDisabled={computeIsSaveDisabled(
                getChecklistArrays('identification', verificationData?.identification_checks),
                verificationData?.identification_status,
                verificationData?.id_errors,
                idUpload.file,
                canReplaceInClarificationStep1,
              )}
              isDiscardDisabled={
                verificationData?.status !== VerificationStatus.STEP_1_REVIEW ||
                (getChecklistArrays('identification', verificationData?.identification_checks).valid.length === 0 &&
                  getChecklistArrays('identification', verificationData?.identification_checks).err.length === 0)
              }
            />

            {/* Handwerkskarte */}
            {showHandwerkskartePanel && (
              <DocumentAccordionPanel
                documentType="handwerkskarte"
                label={t('workshop_verification_request:handwerkskarte.label')}
                checksData={verificationData?.handwerkskarte_checks}
                documentFile={verificationData?.handwerkskarte}
                uploadedFile={hkUpload.file}
                isReplacing={hkUpload.isReplacing}
                buttonsLocked={derived.buttonsLockedStepOne}
                isClarificationReplacement={canReplaceInClarificationStep1}
                canReplace={derived.isStep1 && derived.isOpenTaskAssignedToCurrentUser && !derived.isInClarification}
                canReplaceCompleted={!!canReplaceDocumentsCompletedVerification}
                isUploadPending={mutations.fileUploadMutation.isPending}
                isSavePending={mutations.updateDocsMutation.isPending || workshopVerificationQuery.isPending}
                isDisabled={derived.isRejected && verificationData?.handwerkskarte?.id === null}
                currentStatus={verificationData?.status}
                isStep1={derived.isStep1}
                statusIcon={
                  <DocumentStatusIcon
                    notTouched={!verificationData?.handwerkskarte_status && (verificationData?.hk_errors?.length ?? 0) === 0}
                    hasErrors={(verificationData?.hk_errors?.length ?? 0) > 0}
                    documentStatus={verificationData?.handwerkskarte_status}
                    hasDocument
                    isStep1={derived.isStep1}
                    currentStatus={verificationData?.status}
                  />
                }
                onOpenReplacement={onOpenHkReplacement}
                onDownload={makeDownloadHandler(verificationData?.handwerkskarte?.id, verificationData?.handwerkskarte ?? undefined)}
                onDiscard={() => {
                  clearChecklistForDoc('handwerkskarte');
                  hkUpload.reset();
                }}
                onSave={makeDocumentSubmitHandler('handwerkskarte', hkUpload.file?.uuid || undefined, hkUpload.reset)}
                isSaveDisabled={computeIsSaveDisabled(
                  getChecklistArrays('handwerkskarte', verificationData?.handwerkskarte_checks),
                  verificationData?.handwerkskarte_status,
                  verificationData?.hk_errors,
                  hkUpload.file,
                  canReplaceInClarificationStep1,
                )}
                isDiscardDisabled={
                  verificationData?.status !== VerificationStatus.STEP_1_REVIEW ||
                  (getChecklistArrays('handwerkskarte', verificationData?.handwerkskarte_checks).valid.length === 0 &&
                    getChecklistArrays('handwerkskarte', verificationData?.handwerkskarte_checks).err.length === 0)
                }
              />
            )}

            {/* Power of Attorney */}
            <DocumentAccordionPanel
              documentType="power_of_attorney"
              label={t('workshop_verification_request:poa.label')}
              checksData={verificationData?.power_of_attorney_checks}
              documentFile={verificationData?.power_of_attorney}
              uploadedFile={poaUpload.file}
              isReplacing={poaUpload.isReplacing}
              buttonsLocked={derived.buttonsLockedStepTwo}
              isClarificationReplacement={canReplaceInClarificationStep2}
              canReplace={derived.isOpenTaskAssignedToCurrentUser && !derived.isInClarification}
              canReplaceCompleted={!!canReplaceDocumentsCompletedVerification}
              isUploadPending={mutations.fileUploadMutation.isPending}
              isSavePending={mutations.updateDocsMutation.isPending || workshopVerificationQuery.isPending}
              isDisabled={
                derived.isStep1 ||
                verificationData?.status === VerificationStatus.WAITING_ON_POA ||
                (derived.isRejected && verificationData?.power_of_attorney?.id === null)
              }
              currentStatus={verificationData?.status}
              isStep1={derived.isStep1}
              statusIcon={
                <DocumentStatusIcon
                  notTouched={!!derived.poaNotTouched}
                  hasErrors={(verificationData?.poa_errors?.length ?? 0) > 0}
                  documentStatus={verificationData?.power_of_attorney_status}
                  hasDocument={!!verificationData?.power_of_attorney?.id}
                  isStep1={derived.isStep1}
                  currentStatus={verificationData?.status}
                />
              }
              onOpenReplacement={onOpenPoaReplacement}
              onDownload={makeDownloadHandler(verificationData?.power_of_attorney?.id, verificationData?.power_of_attorney)}
              onDiscard={() => {
                clearChecklistForDoc('power_of_attorney');
                poaUpload.reset();
              }}
              onSave={makeDocumentSubmitHandler('power_of_attorney', poaUpload.file?.uuid || undefined, poaUpload.reset)}
              isSaveDisabled={computeIsSaveDisabled(
                getChecklistArrays('power_of_attorney', verificationData?.power_of_attorney_checks),
                verificationData?.power_of_attorney_status,
                verificationData?.poa_errors,
                poaUpload.file,
                canReplaceInClarificationStep2,
              )}
              isDiscardDisabled={
                verificationData?.status !== VerificationStatus.STEP_2_REVIEW ||
                (getChecklistArrays('power_of_attorney', verificationData?.power_of_attorney_checks).valid.length === 0 &&
                  getChecklistArrays('power_of_attorney', verificationData?.power_of_attorney_checks).err.length === 0)
              }
            />
          </Accordion>

          {/* Action Buttons */}
          {!derived.isCompleted && (
            <VerificationActionButtons
              status={verificationData?.status}
              isOpenTaskAssignedToCurrentUser={derived.isOpenTaskAssignedToCurrentUser}
              canReplaceInClarificationStep1={canReplaceInClarificationStep1}
              canReplaceInClarificationStep2={canReplaceInClarificationStep2}
              isInClarificationStep1={derived.isInClarificationStep1}
              isInClarificationStep2={derived.isInClarificationStep2}
              isStep1={derived.isStep1}
              onStepOne={derived.onStepOne}
              onStepTwo={derived.onStepTwo}
              isSystemPartnerFull={derived.isSystemPartnerFull}
              shouldDisableSendPoa={derived.shouldDisableSendPoa}
              isClarificationButtonDisabled={derived.isClarificationButtonDisabled}
              identificationStatus={verificationData?.identification_status}
              businessLicenceStatus={verificationData?.business_licence_status}
              hasHandwerkskarte={verificationData?.handwerkskarte?.id != null}
              handwerkskarteStatus={verificationData?.handwerkskarte_status}
              powerOfAttorneyStatus={verificationData?.power_of_attorney_status}
              isClarificationPending={mutations.clarificationMutation.isPending}
              isPoaPending={mutations.triggerPoaMutation.isPending}
              isSpfPending={mutations.triggerSpfMailMutation.isPending}
              onSendPoaEmail={() => mutations.triggerPoaMutation.mutate()}
              onSendSpfEmail={actions.onTriggerSpfMail}
              onClarification={() => actions.onClarification(derived.onStepOne ? 'step1' : 'step2')}
              onReject={onOpenReject}
              onApprove={onOpenApprove}
              onSubmitFromClarification={() => {
                setReasonForReplacement('');
                onOpenClarificationReplacement();
              }}
              hasStepOneErrors={
                (verificationData?.id_errors?.length ?? 0) > 0 ||
                (verificationData?.bl_errors?.length ?? 0) > 0 ||
                (verificationData?.hk_errors?.length ?? 0) > 0
              }
              hasStepTwoErrors={(verificationData?.poa_errors?.length ?? 0) > 0}
              canManuallyUploadPoa={canManuallyUploadPoa}
              isManualPoaUploadAllowed={derived.isManualPoaUploadAllowed}
              onManuallyUploadPoa={onOpenManualPoaUpload}
            />
          )}
        </Stack>
      </Card>

      {/* Activity Log */}
      <TasksTableCard tasks={tasks} isLoading={workshopVerificationQuery.isPending} m={6} />
      <ActivityLogCard
        queryKey={useWorkshopVerificationRequestQuery.getKey(workshopId)}
        resourceKey="workshop_verification_request"
        activities={verificationData?.activities as Activity[]}
        account_id={workshopIdNumber}
        m={6}
      />

      {/* Modals */}
      <ManualPoaUploadModal verificationId={verificationData?.id} isOpen={isManualPoaUploadOpen} onClose={onCloseManualPoaUpload} />
      <VerificationModals
        isRejectOpen={isRejectOpen}
        onRejectClose={onCloseReject}
        onRejectConfirm={() => actions.rejectVerification({ onSuccess: onCloseReject })}
        isRejectLoading={mutations.approveMutation.isPending}
        isApproveOpen={isApproveOpen}
        onApproveClose={onCloseApprove}
        onApproveConfirm={() => actions.approveStep({ onSuccess: onCloseApprove })}
        isApproveLoading={mutations.approveMutation.isPending}
        isOnStepOne={verificationData?.status === VerificationStatus.STEP_1_REVIEW}
        isIdReplacementOpen={isIdReplacementOpen}
        onIdReplacementClose={onCloseIdReplacement}
        onIdReplacementConfirm={onIdReplacement}
        isBlReplacementOpen={isBlReplacementOpen}
        onBlReplacementClose={onCloseBlReplacement}
        onBlReplacementConfirm={onBlReplacement}
        isPoaReplacementOpen={isPoaReplacementOpen}
        onPoaReplacementClose={onClosePoaReplacement}
        onPoaReplacementConfirm={onPoaReplacement}
        isHandelskarteReplacementOpen={isHandelskarteReplacementOpen}
        onHandelskarteReplacementClose={onCloseHkReplacement}
        onHandelskarteReplacementConfirm={onHandelskarteReplacement}
        isVerifiedReplacementOpen={isVerifiedReplacementOpen}
        onVerifiedReplacementClose={onCloseVerifiedReplacement}
        onVerifiedReplacementConfirm={handleVerifiedReplacementConfirm}
        reasonForReplacement={reasonForReplacement}
        onReasonChange={setReasonForReplacement}
        isClarificationReplacementOpen={isClarificationReplacementOpen}
        onClarificationReplacementClose={onCloseClarificationReplacement}
        onClarificationReplacementConfirm={() =>
          actions.submitFromClarification(reasonForReplacement, { onSuccess: onCloseClarificationReplacement })
        }
      />
    </FormProvider>
  );
};

export default WorkshopVerificationDetailPage;
